import { Box, Card, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Business_Profile_Enum } from '@kheops/graphql';
import { RoutePaths } from '../../routes/AppRoutes';
import { Member, currentChannelAtom, currentContractAtom, showChannelMembersAtom } from '../state';
import useOpenable from '../../hooks/useOpenable';
import TopAppBar from '../../common/components/TopAppBar';
import ChannelCompanyMemberList from './ChannelCompanyMemberList';
import AddMemberDialog from './AddMemberDialog';

export default function ChannelMemberList(): React.JSX.Element {
  const { t } = useTranslation(['common', 'chat']);
  const currentChannel = useAtomValue(currentChannelAtom);
  const currentContract = useAtomValue(currentContractAtom);
  const setShowChannelMembers = useSetAtom(showChannelMembersAtom);
  const { close, open, isOpen } = useOpenable();

  const [buyingCompanyMembers, supplyingCompanyMembers] = useMemo(() => {
    const distinctBuyingMembers = new Set<string>();
    const distinctSupplyingMembers = new Set<string>();
    const userInChannel = (user: Member): boolean => !!currentChannel?.members.some(({ userId }) => userId === user.id);

    return [
      currentContract?.buying_company.role_assignments
        .map(({ user }) => user)
        .filter((user) => {
          if (!distinctBuyingMembers.has(user.id)) {
            distinctBuyingMembers.add(user.id);

            return true;
          }
          return false;
        })
        .filter(userInChannel),
      currentContract?.supplying_company.role_assignments
        .map(({ user }) => user)
        .filter((user) => {
          if (!distinctSupplyingMembers.has(user.id)) {
            distinctSupplyingMembers.add(user.id);

            return true;
          }
          return false;
        })
        .filter(userInChannel),
    ];
  }, [currentContract, currentChannel?.lastMessage]);

  if (!currentChannel || !currentContract) {
    return <></>;
  }

  return (
    <>
      <TopAppBar
        configuration="small"
        title={t('chat:members_of_conversation')}
        backTo={RoutePaths.ROOT}
        startAction={(
          <Button onClick={() => setShowChannelMembers(false)} size="large">
            <ArrowBackIcon />
          </Button>
        )}
        trailingAction={(
          <Button variant="text" onClick={open}>
            <GroupAddOutlined />
          </Button>
        )}
        sx={{
          position: {
            xs: 'fixed',
            lg: 'none',
          },
        }}
      />
      <Card
        className="Kheops-ChatListPage-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: {
              xs: 'none',
              lg: 'flex',
            },
            mb: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="text" onClick={() => setShowChannelMembers(false)}>
              <Close />
            </Button>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="bodyMedium">
                {t('chat:members_of_conversation')}
              </Typography>
              <Typography variant="bodySmall" color="secondary">
                {t('chat:N_member', { count: currentChannel.memberCount })}
              </Typography>
            </Box>
          </Box>
          <Button variant="text" onClick={open}>
            <GroupAddOutlined />
          </Button>
        </Box>
        <Box sx={{ overflow: 'auto' }}>
          <ChannelCompanyMemberList company={currentContract.buying_company} companyMembers={buyingCompanyMembers!} businessProfile={Business_Profile_Enum.Buyer} />
          <ChannelCompanyMemberList company={currentContract.supplying_company} companyMembers={supplyingCompanyMembers!} businessProfile={Business_Profile_Enum.Supplier} />
        </Box>
      </Card>
      {isOpen && (
        <AddMemberDialog
          channel={currentChannel}
          contract={currentContract}
          isOpen={isOpen}
          onClose={close}
        />
      )}
    </>
  );
}
